/**
 * Swarm Learning Loop Dashboard Component
 * 
 * Modern dashboard layout inspired by shadcn/ui blocks
 */

import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, TrendingUp, Activity, Users, FileText, Play, Plus, Sparkles, Zap, Target, X, Trash2 } from 'lucide-react'
import { swarmAPI, type DashboardResponse, type SearchResult } from '../lib/api_swarm'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { useLocale } from '../i18n/LocaleContext'

interface SwarmDashboardProps {
  apiBaseUrl?: string
}

const SwarmDashboard: React.FC<SwarmDashboardProps> = () => {
  const { t } = useLocale()
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState(30)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [showCreateRun, setShowCreateRun] = useState(false)
  const [creatingRun, setCreatingRun] = useState(false)
  const [runGoal, setRunGoal] = useState('')
  const [runTasks, setRunTasks] = useState<Array<{
    id: string
    name: string
    description: string
    owner_agent_role: string
    inputs: string[]
    outputs: string[]
    dependencies: string[]
    acceptance_criteria: string[]
  }>>([
    {
      id: 'task_001',
      name: '',
      description: '',
      owner_agent_role: 'Implementer',
      inputs: [],
      outputs: [],
      dependencies: [],
      acceptance_criteria: ['']
    }
  ])

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await swarmAPI.getDashboard(days)
      setDashboard(data)
    } catch (err: any) {
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setError(t('swarm.error.notFound'))
      } else {
        setError(err.message || t('swarm.error.load'))
      }
    } finally {
      setLoading(false)
    }
  }, [days, t])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    
    setSearching(true)
    try {
      const results = await swarmAPI.searchExperiences({
        query: searchQuery,
        n_results: 5
      })
      setSearchResults(results)
    } catch (err: any) {
      setError(err.message || t('swarm.error.search'))
    } finally {
      setSearching(false)
    }
  }

  const handleCreateRun = async () => {
    if (!runGoal.trim()) {
      setError(t('swarm.validation.goal'))
      return
    }

    // Validate tasks
    const validTasks = runTasks.filter(t => t.name.trim() && t.id.match(/^task_\d{3}$/))
    if (validTasks.length === 0) {
      setError(t('swarm.validation.tasks'))
      return
    }

    // Validate unique task IDs
    const taskIds = new Set(validTasks.map(t => t.id))
    if (taskIds.size !== validTasks.length) {
      setError(t('swarm.validation.uniqueIds'))
      return
    }

    // Validate acceptance criteria
    for (const task of validTasks) {
      const criteria = task.acceptance_criteria.filter(c => c.trim())
      if (criteria.length === 0) {
        setError(t('swarm.validation.acceptance', { id: task.id }))
        return
      }
    }

    // Validate dependencies exist
    const validTaskIds = new Set(validTasks.map(t => t.id))
    for (const task of validTasks) {
      for (const dep of task.dependencies) {
        if (dep && !validTaskIds.has(dep)) {
          setError(t('swarm.validation.dependency', { id: task.id, dep }))
          return
        }
      }
    }

    setCreatingRun(true)
    setError(null)
    try {
      const result = await swarmAPI.createRun({
        goal: runGoal,
        tasks: validTasks.map(task => ({
          id: task.id,
          name: task.name,
          description: task.description || undefined,
          owner_agent_role: task.owner_agent_role,
          inputs: task.inputs.filter(i => i.trim()),
          outputs: task.outputs.filter(o => o.trim()),
          dependencies: task.dependencies.filter(d => d.trim()),
          acceptance_criteria: task.acceptance_criteria.filter(c => c.trim())
        }))
      })
      
      alert(`${t('swarm.alert.created', { id: result.run_id })}\n${t('swarm.alert.status', { status: result.status })}`)
      setShowCreateRun(false)
      setRunGoal('')
      setRunTasks([{
        id: 'task_001',
        name: '',
        description: '',
        owner_agent_role: 'Implementer',
        inputs: [],
        outputs: [],
        dependencies: [],
        acceptance_criteria: ['']
      }])
      // Reload dashboard after a moment
      setTimeout(() => loadDashboard(), 2000)
    } catch (err: any) {
      setError(err.message || t('swarm.error.create'))
    } finally {
      setCreatingRun(false)
    }
  }

  const addTask = () => {
    const nextId = `task_${String(runTasks.length + 1).padStart(3, '0')}`
    setRunTasks([...runTasks, {
      id: nextId,
      name: '',
      description: '',
      owner_agent_role: 'Implementer',
      inputs: [],
      outputs: [],
      dependencies: [],
      acceptance_criteria: ['']
    }])
  }

  const removeTask = (index: number) => {
    setRunTasks(runTasks.filter((_, i) => i !== index))
  }

  const updateTask = (index: number, field: string, value: any) => {
    const updated = [...runTasks]
    updated[index] = { ...updated[index], [field]: value }
    setRunTasks(updated)
  }

  const addTaskArrayItem = (taskIndex: number, field: 'inputs' | 'outputs' | 'dependencies' | 'acceptance_criteria') => {
    const updated = [...runTasks]
    updated[taskIndex][field] = [...updated[taskIndex][field], '']
    setRunTasks(updated)
  }

  const removeTaskArrayItem = (taskIndex: number, field: 'inputs' | 'outputs' | 'dependencies' | 'acceptance_criteria', itemIndex: number) => {
    const updated = [...runTasks]
    updated[taskIndex][field] = updated[taskIndex][field].filter((_, i) => i !== itemIndex)
    setRunTasks(updated)
  }

  const updateTaskArrayItem = (taskIndex: number, field: 'inputs' | 'outputs' | 'dependencies' | 'acceptance_criteria', itemIndex: number, value: string) => {
    const updated = [...runTasks]
    updated[taskIndex][field][itemIndex] = value
    setRunTasks(updated)
  }

  const getAvailableTaskIds = (currentTaskIndex: number) => {
    return runTasks
      .map((t, i) => i !== currentTaskIndex ? t.id : null)
      .filter(id => id !== null) as string[]
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASS': return 'bg-green-500/10 text-green-600 border-green-500/20'
      case 'WARN': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
      case 'FAIL': return 'bg-red-500/10 text-red-600 border-red-500/20'
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PASS': return t('swarm.badge.pass')
      case 'WARN': return t('swarm.badge.warn')
      case 'FAIL': return t('swarm.badge.fail')
      default: return '?'
    }
  }

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="animate-spin w-12 h-12 mx-auto mb-4 text-purple-600" />
          <p className="text-xl">{t('swarm.loading')}</p>
        </div>
      </div>
    )
  }

  if (error && !dashboard) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">{t('swarm.error')}</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={loadDashboard}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors"
            >
              {t('swarm.retry')}
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!dashboard) {
    return <div className="p-8">{t('swarm.noData')}</div>
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>
            <Sparkles style={{ width: '2rem', height: '2rem', color: '#9333ea' }} />
            {t('swarm.title')}
          </h2>
          <p>
            {t('swarm.subtitle')}
          </p>
        </div>
        <div className="dashboard-actions">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-10 px-4 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={7}>{t('swarm.range.7')}</option>
            <option value={30}>{t('swarm.range.30')}</option>
            <option value={90}>{t('swarm.range.90')}</option>
          </select>
          <button
            onClick={() => setShowCreateRun(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            <Plus className="w-4 h-4" />
            {t('swarm.newRun')}
          </button>
          <button
            onClick={loadDashboard}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('swarm.refresh')}
          </button>
        </div>
      </div>

      {/* Stats Cards - Grid Layout */}
      <div className="dashboard-grid">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('swarm.stat.totalRuns')}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.stats.total_runs}</div>
            <p className="text-xs text-muted-foreground">
              {t('swarm.stat.totalRunsDesc')}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('swarm.stat.successRate')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(dashboard.stats.success_rate * 100).toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {t('swarm.stat.successRateDesc')}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('swarm.stat.avgScore')}</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.stats.average_score.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">
              {t('swarm.stat.avgScoreDesc')}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('swarm.stat.totalTasks')}</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.stats.total_tasks}</div>
            <p className="text-xs text-muted-foreground">
              {t('swarm.stat.totalTasksDesc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown and Agent Performance - Side by Side */}
      <div className="dashboard-grid-2col">
        <Card>
          <CardHeader>
            <CardTitle>{t('swarm.agents.title')}</CardTitle>
            <CardDescription>{t('swarm.agents.desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboard.agent_performance.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('swarm.agents.empty')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 text-sm font-medium">{t('swarm.table.agent')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.tasks')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.success')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.score')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.passed')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.warned')}</th>
                      <th className="text-right p-2 text-sm font-medium">{t('swarm.table.failed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.agent_performance.map((agent) => (
                      <tr key={agent.agent} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{agent.agent}</td>
                        <td className="p-2 text-right text-sm">{agent.total_tasks}</td>
                        <td className="p-2 text-right text-sm">
                          <span className={agent.success_rate > 0.8 ? 'text-green-600 font-semibold' : agent.success_rate > 0.6 ? 'text-yellow-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {(agent.success_rate * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-right text-sm">{agent.average_score.toFixed(1)}</td>
                        <td className="p-2 text-right text-sm text-green-600">{agent.passed_tasks}</td>
                        <td className="p-2 text-right text-sm text-yellow-600">{agent.warned_tasks}</td>
                        <td className="p-2 text-right text-sm text-red-600">{agent.failed_tasks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('swarm.breakdown.title')}</CardTitle>
            <CardDescription>{t('swarm.breakdown.desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('swarm.breakdown.passed')}</span>
                  <span className="text-sm font-semibold text-green-600">
                    {dashboard.stats.passed_runs} ({dashboard.stats.total_runs > 0 
                      ? ((dashboard.stats.passed_runs / dashboard.stats.total_runs) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${dashboard.stats.total_runs > 0 ? (dashboard.stats.passed_runs / dashboard.stats.total_runs) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('swarm.breakdown.warned')}</span>
                  <span className="text-sm font-semibold text-yellow-600">
                    {dashboard.stats.warned_runs} ({dashboard.stats.total_runs > 0
                      ? ((dashboard.stats.warned_runs / dashboard.stats.total_runs) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-yellow-600 h-2 rounded-full transition-all"
                    style={{ width: `${dashboard.stats.total_runs > 0 ? (dashboard.stats.warned_runs / dashboard.stats.total_runs) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('swarm.breakdown.failed')}</span>
                  <span className="text-sm font-semibold text-red-600">
                    {dashboard.stats.failed_runs} ({dashboard.stats.total_runs > 0
                      ? ((dashboard.stats.failed_runs / dashboard.stats.total_runs) * 100).toFixed(1)
                      : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full transition-all"
                    style={{ width: `${dashboard.stats.total_runs > 0 ? (dashboard.stats.failed_runs / dashboard.stats.total_runs) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Runs - Full Width */}
      <Card>
        <CardHeader>
          <CardTitle>{t('swarm.runs.title')}</CardTitle>
          <CardDescription>{t('swarm.runs.desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {dashboard.recent_runs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">{t('swarm.runs.empty')}</p>
              </div>
            ) : (
              dashboard.recent_runs.map((run) => (
                <div
                  key={run.run_id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(run.overall_status)}`}>
                      {getStatusBadge(run.overall_status)}
                    </span>
                    <div>
                      <div className="font-mono text-sm font-medium">{run.run_id}</div>
                      <div className="text-xs text-muted-foreground">{run.date}</div>
                    </div>
                    <div className="text-sm text-muted-foreground max-w-md truncate">
                      {run.goal}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('swarm.runs.score')} </span>
                      <span className="font-semibold">{run.overall_score}/100</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('swarm.runs.tasks')} </span>
                      <span className="text-green-600">{run.passed_tasks}</span>
                      <span className="text-muted-foreground">/{run.total_tasks}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vector Store Search */}
      {dashboard.vector_store_stats && dashboard.vector_store_stats.embedding_model_available && (
        <Card>
          <CardHeader>
            <CardTitle>{t('swarm.search.title')}</CardTitle>
            <CardDescription>{t('swarm.search.desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('swarm.search.placeholder')}
                className="flex-1 h-10 px-4 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              >
                <Search className="w-4 h-4" />
                {searching ? t('swarm.search.searching') : t('swarm.search.button')}
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-sm">{t('swarm.search.results')}</h3>
                {searchResults.map((result) => (
                  <div key={result.run_id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">{result.run_id}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(result.overall_status)}`}>
                        {getStatusBadge(result.overall_status)} {result.overall_score}/100
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">{result.goal}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Run Modal */}
      {showCreateRun && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('swarm.create.title')}</CardTitle>
                <button
                  onClick={() => {
                    setShowCreateRun(false)
                    setRunGoal('')
                    setRunTasks([{
                      id: 'task_001',
                      name: '',
                      description: '',
                      owner_agent_role: 'Implementer',
                      inputs: [],
                      outputs: [],
                      dependencies: [],
                      acceptance_criteria: ['']
                    }])
                    setError(null)
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                  {error}
                </div>
              )}

              {/* Goal */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('swarm.create.goal')}</label>
                <textarea
                  value={runGoal}
                  onChange={(e) => setRunGoal(e.target.value)}
                  placeholder={t('swarm.create.goalPlaceholder')}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={3}
                />
              </div>

              {/* Tasks */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium">{t('swarm.create.tasks')}</label>
                  <button
                    onClick={addTask}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
                  >
                    <Plus className="w-4 h-4" />
                    {t('swarm.create.addTask')}
                  </button>
                </div>

                <div className="space-y-4">
                  {runTasks.map((task, taskIndex) => (
                    <Card key={taskIndex}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{t('swarm.create.taskLabel', { n: taskIndex + 1 })}</CardTitle>
                          {runTasks.length > 1 && (
                            <button
                              onClick={() => removeTask(taskIndex)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium mb-1">{t('swarm.create.taskId')}</label>
                            <input
                              type="text"
                              value={task.id}
                              onChange={(e) => updateTask(taskIndex, 'id', e.target.value)}
                              placeholder={t('swarm.create.taskIdPlaceholder')}
                              pattern="^task_\d{3}$"
                              className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('swarm.create.taskIdHint')}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">{t('swarm.create.agent')} *</label>
                            <select
                              value={task.owner_agent_role}
                              onChange={(e) => updateTask(taskIndex, 'owner_agent_role', e.target.value)}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="Implementer">Implementer</option>
                              <option value="Researcher">Researcher</option>
                              <option value="Ops">Ops</option>
                              <option value="Reviewer">Reviewer</option>
                              <option value="Architect">Architect</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">{t('swarm.create.name')}</label>
                          <input
                            type="text"
                            value={task.name}
                            onChange={(e) => updateTask(taskIndex, 'name', e.target.value)}
                            placeholder={t('swarm.create.namePlaceholder')}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">{t('swarm.create.description')}</label>
                          <textarea
                            value={task.description}
                            onChange={(e) => updateTask(taskIndex, 'description', e.target.value)}
                            placeholder={t('swarm.create.taskPlaceholder')}
                            rows={2}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>

                        {/* Dependencies */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-medium">{t('swarm.create.dependencies')}</label>
                            <button
                              onClick={() => addTaskArrayItem(taskIndex, 'dependencies')}
                              className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded text-sm"
                            >
                              {t('swarm.create.add')}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {task.dependencies.map((dep, depIndex) => (
                              <div key={depIndex} className="flex gap-2">
                                <select
                                  value={dep}
                                  onChange={(e) => updateTaskArrayItem(taskIndex, 'dependencies', depIndex, e.target.value)}
                                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                >
                                  <option value="">{t('swarm.create.selectDependency')}</option>
                                  {getAvailableTaskIds(taskIndex).map(id => (
                                    <option key={id} value={id}>{id}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => removeTaskArrayItem(taskIndex, 'dependencies', depIndex)}
                                  className="px-2 text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Inputs */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-medium">{t('swarm.create.inputs')}</label>
                            <button
                              onClick={() => addTaskArrayItem(taskIndex, 'inputs')}
                              className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded text-sm"
                            >
                              {t('swarm.create.add')}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {task.inputs.map((input, inputIndex) => (
                              <div key={inputIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={input}
                                  onChange={(e) => updateTaskArrayItem(taskIndex, 'inputs', inputIndex, e.target.value)}
                                  placeholder={t('swarm.create.inputPlaceholder')}
                                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                                <button
                                  onClick={() => removeTaskArrayItem(taskIndex, 'inputs', inputIndex)}
                                  className="px-2 text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Outputs */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-medium">{t('swarm.create.outputs')}</label>
                            <button
                              onClick={() => addTaskArrayItem(taskIndex, 'outputs')}
                              className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded text-sm"
                            >
                              {t('swarm.create.add')}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {task.outputs.map((output, outputIndex) => (
                              <div key={outputIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={output}
                                  onChange={(e) => updateTaskArrayItem(taskIndex, 'outputs', outputIndex, e.target.value)}
                                  placeholder={t('swarm.create.outputPlaceholder')}
                                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                                <button
                                  onClick={() => removeTaskArrayItem(taskIndex, 'outputs', outputIndex)}
                                  className="px-2 text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Acceptance Criteria */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-medium">{t('swarm.create.acceptance')}</label>
                            <button
                              onClick={() => addTaskArrayItem(taskIndex, 'acceptance_criteria')}
                              className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded text-sm"
                            >
                              {t('swarm.create.add')}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {task.acceptance_criteria.map((criteria, criteriaIndex) => (
                              <div key={criteriaIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={criteria}
                                  onChange={(e) => updateTaskArrayItem(taskIndex, 'acceptance_criteria', criteriaIndex, e.target.value)}
                                  placeholder={t('swarm.create.acceptancePlaceholder')}
                                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                                {task.acceptance_criteria.length > 1 && (
                                  <button
                                    onClick={() => removeTaskArrayItem(taskIndex, 'acceptance_criteria', criteriaIndex)}
                                    className="px-2 text-red-600 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </CardContent>
            <CardContent className="flex gap-4 pt-0">
              <button
                onClick={() => {
                  setShowCreateRun(false)
                  setRunGoal('')
                  setRunTasks([{
                    id: 'task_001',
                    name: '',
                    description: '',
                    owner_agent_role: 'Implementer',
                    inputs: [],
                    outputs: [],
                    dependencies: [],
                    acceptance_criteria: ['']
                  }])
                  setError(null)
                }}
                className="flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateRun}
                disabled={creatingRun || !runGoal.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              >
                {creatingRun ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('swarm.create.creating')}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    {t('swarm.create.submit')}
                  </>
                )}
              </button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default SwarmDashboard
